import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { BrandSwitcher } from "./BrandSwitcher";
import { Icon, type IconName } from "./Icon";
import "./shell.css";

/**
 * DESIGN_SYSTEM.md §4 — the full primary navigation IA.
 *
 * All six destinations render from Phase 0 (DECISIONS.md SCOPE-003). Only
 * Dashboard and Settings have content; the rest show an honest "arrives in
 * Phase N" state rather than being hidden and bolted on later, which would mean
 * re-laying-out the shell four more times.
 */
const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: "dashboard", end: true, phase: null },
  {
    to: "/ground-truth",
    label: "Ground Truth",
    icon: "groundTruth",
    end: false,
    phase: null,
  },
  {
    to: "/prompts",
    label: "Personas & Prompts",
    icon: "prompts",
    end: false,
    phase: null,
  },
  {
    to: "/captures",
    label: "Captures",
    icon: "clock",
    end: false,
    phase: null,
  },
  {
    to: "/drift",
    label: "Narrative Drift",
    icon: "drift",
    end: false,
    phase: null,
  },
  {
    to: "/actions",
    label: "Actions",
    icon: "actions",
    end: false,
    phase: null,
  },
  {
    to: "/reports",
    label: "Reports",
    icon: "reports",
    end: false,
    phase: null,
  },
  {
    to: "/settings",
    label: "Settings",
    icon: "settings",
    end: false,
    phase: null,
  },
] as const satisfies readonly { icon: IconName; [k: string]: unknown }[];

export function AppShell() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>

      <aside className="shell__sidebar">
        <div className="shell__brandmark">
          <span className="shell__mark" aria-hidden="true">
            <Icon name="echo" size={20} />
          </span>
          <span>
            Fulcro <strong>Echo</strong>
          </span>
        </div>

        <BrandSwitcher />

        <nav className="shell__nav" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={`shell__navitem${item.phase ? " shell__navitem--pending" : ""}`}
              // Carries the label when the rail collapses to icons at tablet
              // width; the visible text is still in the accessibility tree.
              title={item.label}
            >
              <span className="shell__navlabel">
                <Icon name={item.icon} size={20} />
                <span>{item.label}</span>
              </span>
              {item.phase && (
                <span
                  className="badge badge--neutral"
                  title={`Arrives in ${item.phase}`}
                >
                  {item.phase}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="shell__main">
        <header className="shell__topbar">
          {/* Mobile only — the tab bar has no room for the Brand switcher, and
              FR-ADMIN-04 requires it stay reachable at every width. */}
          <div className="shell__topbar-brand">
            <BrandSwitcher />
          </div>
          <div>
            {user?.isPlatformSuperAdmin && (
              // RBAC_MODEL.md §5 — SuperAdmins work inside the same UI, so the
              // elevated state must be unmistakable rather than invisible.
              <span className="badge badge--info">
                <Icon name="shield" size={16} />
                Platform support session
              </span>
            )}
          </div>
          <div className="shell__usermenu">
            <span className="shell__useremail">{user?.email}</span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={async () => {
                await signOut();
                navigate("/login", { replace: true });
              }}
            >
              <Icon name="logOut" size={16} />
              Sign out
            </button>
          </div>
        </header>

        <main className="shell__content" id="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
